<template>
  <div class="quote-product-search" :class="{ open: menuOpen, 'invert-to-top': invertToTop }" ref="searchRoot" @focusout="checkFocusOut">
    <input
      ref="searchInput"
      type="search"
      :value="modelValue"
      placeholder="Item #"
      role="combobox"
      aria-autocomplete="list"
      :aria-controls="`${searchId}-results`"
      :aria-expanded="menuOpen"
      :aria-activedescendant="focusedIndex === null ? undefined : `${searchId}-result-${focusedIndex}`"
      @focus="handleFocus"
      @input="handleInput"
      @keydown="handleKeydown" />
    <img class="search-icon" src="../../assets/search.svg" alt="" />

    <div
      v-if="menuOpen"
      :id="`${searchId}-results`"
      ref="searchResults"
      class="search-results"
      role="listbox"
      :style="{ maxHeight: `${menuMaxHeight}px` }">
      <div
        v-for="(product, index) in matches"
        :key="product.PRODUCTID"
        :id="`${searchId}-result-${index}`"
        class="search-result"
        :class="{ hovering: focusedIndex === index }"
        role="option"
        :aria-selected="focusedIndex === index"
        @mousedown.prevent="selectProduct(product)"
        @mousemove="focusedIndex = index">
        <span class="part-number">{{ product.PARTNUM }}</span>
        <span class="description">{{ product.DESCRIPTION }}</span>
        <span v-if="product.COLOR" class="color">{{ product.COLOR }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { searchQuoteProducts } from '@/composables/useQuoteProductSearch';

const MENU_PADDING = 12;
const PREFERRED_MAX_HEIGHT = 288; // 18rem
const MIN_MENU_HEIGHT = 120;
let nextSearchId = 0;

export default {
  name: 'QuoteProductSearch',
  emits: ['update:modelValue', 'selectProduct'],
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    products: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      searchId: `quote-product-search-${(nextSearchId += 1)}`,
      open: false,
      focusedIndex: null,
      invertToTop: false,
      menuMaxHeight: PREFERRED_MAX_HEIGHT,
      replaceOnNextEdit: false,
    };
  },
  computed: {
    matches() {
      return searchQuoteProducts(this.products, this.modelValue, 15);
    },
    menuOpen() {
      return this.open && this.matches.length > 0;
    },
  },
  watch: {
    menuOpen(isOpen) {
      if (isOpen) {
        this.updateMenuPosition();
        window.addEventListener('resize', this.updateMenuPosition);
        window.addEventListener('scroll', this.updateMenuPosition, true);
      } else {
        this.teardownPositionListeners();
        this.invertToTop = false;
        this.menuMaxHeight = PREFERRED_MAX_HEIGHT;
      }
    },
    matches() {
      if (this.menuOpen) this.updateMenuPosition();
    },
  },
  beforeUnmount() {
    this.teardownPositionListeners();
  },
  methods: {
    handleFocus() {
      this.open = true;
      this.focusedIndex = null;
      this.replaceOnNextEdit = !!this.modelValue;
      this.$nextTick(() => {
        this.$refs.searchInput?.select();
      });
    },
    handleInput(event) {
      this.replaceOnNextEdit = false;
      this.$emit('update:modelValue', event.target.value);
      this.open = true;
      this.focusedIndex = null;
    },
    selectProduct(product) {
      this.$emit('selectProduct', product);
      this.open = false;
      this.focusedIndex = null;
      this.replaceOnNextEdit = false;
    },
    isReplaceEditKey(event) {
      if (event.metaKey || event.ctrlKey || event.altKey) return false;
      if (event.key === 'Backspace' || event.key === 'Delete') return true;
      return event.key.length === 1;
    },
    moveFocusedProduct(direction) {
      if (!this.matches.length) return;

      this.open = true;
      if (this.focusedIndex === null) {
        this.focusedIndex = direction > 0 ? 0 : this.matches.length - 1;
      } else {
        this.focusedIndex = Math.max(0, Math.min(this.focusedIndex + direction, this.matches.length - 1));
      }

      this.$nextTick(() => {
        const option = this.$refs.searchResults?.querySelector('.search-result.hovering');
        option?.scrollIntoView({ block: 'nearest' });
      });
    },
    handleKeydown(event) {
      if (this.replaceOnNextEdit && this.isReplaceEditKey(event)) {
        event.preventDefault();
        this.replaceOnNextEdit = false;
        const nextValue = event.key === 'Backspace' || event.key === 'Delete' ? '' : event.key;
        this.$emit('update:modelValue', nextValue);
        this.open = true;
        this.focusedIndex = null;
        return;
      }

      if (event.key === 'ArrowDown') {
        if (!this.matches.length) return;
        event.preventDefault();
        event.stopPropagation();
        this.replaceOnNextEdit = false;
        this.moveFocusedProduct(1);
      } else if (event.key === 'ArrowUp') {
        if (!this.matches.length) return;
        event.preventDefault();
        event.stopPropagation();
        this.replaceOnNextEdit = false;
        this.moveFocusedProduct(-1);
      } else if (event.key === 'Enter') {
        if (!this.menuOpen || !this.matches.length) return;
        event.preventDefault();
        event.stopPropagation();
        this.selectProduct(this.matches[this.focusedIndex ?? 0]);
      } else if (event.key === 'Escape') {
        this.open = false;
        this.replaceOnNextEdit = false;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'Home' || event.key === 'End') {
        this.replaceOnNextEdit = false;
      }
    },
    checkFocusOut(event) {
      if (!this.$refs.searchRoot?.contains(event.relatedTarget)) {
        this.open = false;
        this.replaceOnNextEdit = false;
      }
    },
    teardownPositionListeners() {
      window.removeEventListener('resize', this.updateMenuPosition);
      window.removeEventListener('scroll', this.updateMenuPosition, true);
    },
    getBottomInset() {
      const stickyActions = this.$el?.closest?.('.quote-page')?.querySelector?.('.quote-actions');
      if (!stickyActions) return MENU_PADDING;
      const actionsRect = stickyActions.getBoundingClientRect();
      if (actionsRect.top >= window.innerHeight) return MENU_PADDING;
      return Math.max(MENU_PADDING, window.innerHeight - actionsRect.top + 8);
    },
    updateMenuPosition() {
      this.$nextTick(() => {
        const input = this.$refs.searchInput;
        if (!input || !this.menuOpen) return;

        const rect = input.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - this.getBottomInset();
        const spaceAbove = rect.top - MENU_PADDING;
        const invert = spaceBelow < MIN_MENU_HEIGHT && spaceAbove > spaceBelow;
        const available = invert ? spaceAbove : spaceBelow;

        this.invertToTop = invert;
        this.menuMaxHeight = Math.max(0, Math.min(PREFERRED_MAX_HEIGHT, available));
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.quote-product-search {
  position: relative;

  &.open {
    z-index: 1001;
  }

  input {
    padding: 0.4rem 1.8rem 0.4rem 0.35rem;

    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      -webkit-appearance: none;
      appearance: none;
      display: none;
    }
  }

  &.open input {
    border-radius: $borderRadius4 $borderRadius4 0 0;
  }

  .search-icon {
    position: absolute;
    right: 0.35rem;
    top: 50%;
    width: 0.9rem;
    transform: translateY(-50%);
    opacity: 0;
    pointer-events: none;
  }

  &:hover .search-icon,
  &:focus-within .search-icon,
  &.open .search-icon {
    opacity: 0.6;
  }

  .search-results {
    position: absolute;
    z-index: 1002;
    top: 100%;
    left: 0;
    right: 0;
    overflow: auto;
    background: white;
    border: 1px solid $inputBorderColor;
    border-top: none;
    border-radius: 0 0 $borderRadius4 $borderRadius4;
  }

  &.open.invert-to-top {
    input {
      border-radius: 0 0 $borderRadius4 $borderRadius4;
    }

    .search-results {
      top: auto;
      bottom: 100%;
      border-top: 1px solid $inputBorderColor;
      border-bottom: none;
      border-radius: $borderRadius4 $borderRadius4 0 0;
    }
  }

  .search-result {
    display: grid;
    grid-template-columns: 7rem 1fr auto;
    gap: 0.5rem;
    padding: 0.45rem 0.6rem;
    cursor: pointer;

    &.hovering,
    &:hover {
      background: $appBackgroundColor;
    }
  }

  .part-number {
    font-weight: 600;
    color: $textColor87;
  }

  .description,
  .color {
    color: $textColor60;
  }
}
</style>
